import React, { useState, useRef, useCallback } from 'react';

interface LogoUploaderProps {
    currentLogo?: string;
    onUpload: (logoData: string) => void;
}

export default function LogoUploader({ currentLogo, onUpload }: LogoUploaderProps) {
    const [preview, setPreview] = useState<string | undefined>(currentLogo);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFile = (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            alert('File size must be less than 2MB');
            return;
        }

        // Read file and convert to base64
        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result as string;
            setPreview(result);
            onUpload(result);
        };
        reader.readAsDataURL(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFile(file);
        }
    };

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) {
                    handleFile(file);
                    break;
                }
            }
        }
    }, []);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFile(file);
        }
    };

    const handleRemove = () => {
        setPreview(undefined);
        onUpload('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Logo</label>

            {preview ? (
                <div className="relative inline-block">
                    <img
                        src={preview}
                        alt="Logo preview"
                        className="h-24 w-auto max-w-xs object-contain border border-border rounded-lg p-2 bg-surface"
                    />
                    <button
                        onClick={handleRemove}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition"
                    >
                        ×
                    </button>
                </div>
            ) : (
                <div
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onPaste={handlePaste}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${isDragging
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary hover:bg-surface'
                        }`}
                    onClick={() => fileInputRef.current?.click()}
                    tabIndex={0}
                >
                    <div className="space-y-2">
                        <div className="text-4xl">📁</div>
                        <div className="text-sm text-text-secondary">
                            <span className="font-semibold text-primary">Click to upload</span>, drag and drop,
                        </div>
                        <div className="text-sm text-text-secondary">
                            or <span className="font-semibold text-primary">paste</span> from clipboard
                        </div>
                        <div className="text-xs text-text-secondary">
                            PNG, JPG, SVG up to 2MB
                        </div>
                    </div>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
            />
        </div>
    );
}
